import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileCode, Copy, Check } from 'lucide-react';

export const XCToM3UConverter = () => {
    const [server, setServer] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [outputType, setOutputType] = useState('m3u_plus'); // m3u_plus or m3u
    const [streamType, setStreamType] = useState('ts'); // ts or hls

    const [generatedUrl, setGeneratedUrl] = useState('');
    const [copied, setCopied] = useState(false);

    const generate = () => {
        if (!server || !username || !password) return;

        // Clean server URL (remove trailing slash)
        const cleanServer = server.trim().replace(/\/$/, '');
        // Ensure http/https
        const finalServer = cleanServer.startsWith('http') ? cleanServer : `http://${cleanServer}`;

        const url = `${finalServer}/get.php?username=${username.trim()}&password=${password.trim()}&type=${outputType}&output=${streamType}`;
        setGeneratedUrl(url);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                    <FileCode className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Xtream Codes → M3U</h2>
                    <p className="text-muted-foreground">Generate a playlist link from your login credentials.</p>
                </div>
            </div>

            <div className="glass p-6 rounded-2xl border border-white/10 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2 text-gray-300">Server URL / Portal URL</label>
                        <input
                            type="text"
                            value={server}
                            onChange={(e) => setServer(e.target.value)}
                            placeholder="http://my-iptv-server.com:8080"
                            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/50 outline-none transition-all font-mono text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-300">Username</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/50 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-300">Password</label>
                        <input
                            type="text"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/50 outline-none transition-all"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase">Playlist Format</label>
                        <div className="flex bg-black/40 p-1 rounded-lg border border-white/5">
                            <button
                                onClick={() => setOutputType('m3u_plus')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${outputType === 'm3u_plus' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                            >
                                M3U Plus (Meta)
                            </button>
                            <button
                                onClick={() => setOutputType('m3u')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${outputType === 'm3u' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                            >
                                Simple M3U
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase">Stream Format</label>
                        <div className="flex bg-black/40 p-1 rounded-lg border border-white/5">
                            <button
                                onClick={() => setStreamType('ts')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${streamType === 'ts' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                            >
                                MPEG-TS (.ts)
                            </button>
                            <button
                                onClick={() => setStreamType('hls')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${streamType === 'hls' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                            >
                                HLS (.m3u8)
                            </button>
                        </div>
                    </div>
                </div>

                <button
                    onClick={generate}
                    disabled={!server || !username || !password}
                    className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                >
                    Generate Link
                </button>
            </div>

            {generatedUrl && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass border border-white/10 rounded-2xl p-6"
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Your M3U URL</span>
                        <button
                            onClick={copyToClipboard}
                            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors font-medium text-sm"
                        >
                            {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
                        </button>
                    </div>
                    <div className="bg-black/40 p-4 rounded-xl border border-white/5 font-mono text-sm break-all text-blue-300 selection:bg-blue-500/30">
                        {generatedUrl}
                    </div>
                </motion.div>
            )}
        </div>
    );
};
